import React from 'react';
import HashtagSelector from '../../shared/components/HashtagSelector';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  selectedHashtags: string[];
  onHashtagChange: (tags: string[]) => void;
  onCancel: () => void;
  onPost: () => void;
  isPostDisabled?: boolean;
  /** Optional fixed width of the sidebar (px). Defaults to 240. */
  widthPx?: number;
  /** Optional sticky offset (px) to match your header height. Defaults to 64. */
  stickyTopPx?: number;

  /** Posts left for today (for label: "Post (# left)") */
  postsLeft?: number;
}

const SidebarGalleryPosting: React.FC<SidebarProps> = ({
  selectedHashtags,
  onHashtagChange,
  onCancel,
  onPost,
  isPostDisabled = false,
  widthPx = 225,
  stickyTopPx = 64,
  postsLeft,
}) => {
  const { t } = useTranslation();
  const disabled = !!isPostDisabled;

  const leftLabel =
    typeof postsLeft === 'number'
      ? ' ' + t('galleryPostingSidebar.leftLabel', { count: postsLeft })
      : '';

      return (
        <div
          style={{
            ...sidebarStyle,
            width: widthPx,
            top: `${stickyTopPx}px`,
          }}
        >
          <h2 style={headingStyle}>🖼 {t('galleryPostingSidebar.heading')}</h2>
      
          <HashtagSelector
            context="gallery"
            selectedTags={selectedHashtags}
            onChange={onHashtagChange}
          />
      
          <div style={{ flex: 1 }} />
      
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={onPost}
              disabled={disabled}
              style={{
                ...buttonStyleBase,
                background:
                  disabled
                    ? 'linear-gradient(135deg,#6b7280,#4b5563)'
                    : 'linear-gradient(135deg,#22c55e,#a3e635)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.7 : 1,
              }}
            >
              🚀 {t('galleryPostingSidebar.buttons.post')}
              {leftLabel}
            </button>
      
            <button
              onClick={onCancel}
              style={{
                ...buttonStyleBase,
                background: 'rgba(15,23,42,0.9)',
                color: '#e5e7eb',
                border: '1px solid rgba(148,163,184,0.6)',
              }}
            >
              ❌ {t('galleryPostingSidebar.buttons.cancel')}
            </button>
          </div>
        </div>
      );
      
};

const sidebarStyle: React.CSSProperties = {
  height: 'calc(92vh - 78px)',
  background:
    'linear-gradient(145deg, rgba(4, 7, 15, 0.95), rgba(15,23,42,0.99))',
  color: '#e5e7eb',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  position: 'sticky',
  top: '120px', // matches page header offset
  borderRadius: 20,
  border: '1px solid rgba(148,163,184,0.35)',
  boxShadow: '0 22px 50px rgba(0,0,0,0.8)',
  padding: 20,
};

const buttonStyleBase: React.CSSProperties = {
  padding: '10px 12px',
  width: '100%',
  border: 'none',
  borderRadius: 999,
  cursor: 'pointer',
  color: '#0b1120',
  fontSize: 14,
  fontWeight: 600,
};

const headingStyle: React.CSSProperties = {
  marginBottom: 8,
  fontSize: 18,
  letterSpacing: -0.02,
};

export default SidebarGalleryPosting;
